import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

const STORAGE_KEY = 'dbm_cookie_consent';

/**
 * Cookie consent banner. On mount, checks localStorage; if no decision has
 * been recorded, shows the banner. Accepting initialises PostHog analytics.
 * Declining opts the user out. Value stored as "accepted" | "declined".
 *
 * Every localStorage / analytics call is wrapped because both can genuinely
 * fail in third-party-cookie-blocked contexts (Safari private, incognito,
 * browser extensions). We warn to the console when that happens so bugs
 * are surfaced without breaking the page.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const choice = localStorage.getItem(STORAGE_KEY);
      if (!choice) setVisible(true);
      else if (choice === 'accepted') initAnalytics();
    } catch (err) {
      console.warn('[CookieConsent] localStorage read failed:', err);
    }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'accepted');
    } catch (err) {
      console.warn('[CookieConsent] Could not persist consent choice:', err);
    }
    initAnalytics();
    setVisible(false);
  };

  const decline = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'declined');
    } catch (err) {
      console.warn('[CookieConsent] Could not persist consent choice:', err);
    }
    if (window.posthog?.opt_out_capturing) {
      try {
        window.posthog.opt_out_capturing();
      } catch (err) {
        console.warn('[CookieConsent] PostHog opt-out failed:', err);
      }
    }
    // Update Google Consent Mode — explicit deny (also the default, but be explicit)
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          functionality_storage: 'denied',
          personalization_storage: 'denied',
        });
      } catch (err) {
        console.warn('[CookieConsent] gtag consent update failed:', err);
      }
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-[440px] z-[60] bg-[#001a38] text-white border border-white/10 rounded-2xl shadow-[0_25px_60px_-20px_rgba(0,0,0,0.6)] p-6 md:p-7"
    >
      <div className="flex items-start justify-between gap-4">
        <p className="eyebrow text-[#C5A059]">Cookies</p>
        <button
          onClick={decline}
          aria-label="Close and decline optional cookies"
          className="text-white/50 hover:text-white transition-colors -mr-1 -mt-1"
        >
          <X size={16} />
        </button>
      </div>
      <p className="mt-4 text-sm text-white/80 leading-relaxed">
        We use only the cookies needed to load this site. If you're happy for us to
        also use anonymous analytics to improve it, let us know.{' '}
        <Link to="/privacy" className="gold-underline">Read our privacy notice</Link>.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          data-testid="cookie-accept"
          onClick={accept}
          className="inline-flex items-center rounded-full bg-[#C5A059] text-[#001a38] px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors"
        >
          Accept
        </button>
        <button
          data-testid="cookie-decline"
          onClick={decline}
          className="inline-flex items-center rounded-full border border-white/25 text-white px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:border-white transition-colors"
        >
          Decline
        </button>
      </div>
    </div>
  );
}

function initAnalytics() {
  // PostHog (product analytics)
  if (window.posthog?.opt_in_capturing) {
    try {
      window.posthog.opt_in_capturing();
    } catch (err) {
      console.warn('[CookieConsent] PostHog opt-in failed:', err);
    }
  }
  // Google Analytics 4 — grant analytics_storage so gtag can set cookies + send hits
  if (typeof window.gtag === 'function') {
    try {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        functionality_storage: 'granted',
      });
    } catch (err) {
      console.warn('[CookieConsent] gtag consent update failed:', err);
    }
  }
}
